import { useEffect, useRef, useState } from "react";
import {
  commandFor,
  defaultBindings,
  shortcutsFor,
  typingInto,
  type EventTargetLike,
  type ShortcutBindings,
  type ShortcutCommand,
  type ShortcutHost,
} from "@cuekards/core/shortcuts";

/**
 * The keyboard — the one part of the shortcut arrangement that has to be a
 * renderer.
 *
 * The desktop used to register its keys with Windows and take them in the main
 * process. Nothing registers anywhere now (one-window ticket 06), so on both
 * hosts the renderer listens, the owner of the settings holds the bindings and
 * the commands, and these two hooks are the join. Which is a loss on the old
 * desktop's terms — the keys only work while the cuekards window is focused — and
 * costs almost nothing for the workflow this is built for, because that window
 * *is* the focused one while a camera is pointed at you. What it costs is the
 * hands-free story, and voice commands are what carries that now.
 */

/**
 * Which key each command is on, followed as it changes.
 *
 * Pulled on mount and then kept in step with the broadcast, like every other
 * piece of state a view draws itself from — so rebinding on the settings screen
 * changes what the prompter listens for without either of them knowing about the
 * other.
 *
 * The initial value is the host's built-ins rather than an empty map, so a key
 * pressed in the moment before the first answer arrives does the built-in thing
 * rather than nothing.
 */
export function useShortcutBindings(host: ShortcutHost): ShortcutBindings {
  const [bindings, setBindings] = useState<ShortcutBindings>(() =>
    defaultBindings(shortcutsFor(host)),
  );

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getShortcutState().then((state) => {
      if (!cancelled) setBindings(state.bindings);
    });
    const off = window.cuekards.onShortcutStateChanged((state) =>
      setBindings(state.bindings),
    );
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return bindings;
}

/** The one command the host runs itself rather than sending. */
export interface ShortcutActions {
  /** Re-fetch the document. The renderer's on the web, because the page is what
   * knows whether a take is rolling (FR-17) and whether one is already in
   * flight; the desktop sends it on, because main owns that gate there. */
  refresh: () => void;
}

/**
 * Listen for the shortcuts while the prompter is up.
 *
 * Three rules, and each is load-bearing because the defaults are unmodified
 * keys:
 *
 * - **Not while the user is typing.** {@link typingInto} is what keeps pasting a
 *   script from recording a take on the way past.
 * - **Mounted where it applies and nowhere else.** On the web that is the
 *   prompter, so the setup screen's buttons and the settings screen's rebinding
 *   capture are not competing with it. On the desktop it is the shell, because
 *   there is one window and a stop you have to change pane to reach is not a
 *   stop.
 * - **A match is always swallowed.** Space scrolls a page, the arrows scroll a
 *   page and Enter presses whatever has focus, and all three would happen *as
 *   well as* the command otherwise.
 *
 * A held key repeats, deliberately: holding the nudge or the font key to run
 * several notches is what a repeat is for, and the commands that are not worth
 * repeating are all idempotent.
 *
 * Full screen is not in the table and that is on purpose: `F11` is the browser's
 * own and works already, and a binding of ours would be fighting it.
 */
export function useShortcuts(host: ShortcutHost, actions: ShortcutActions): void {
  const bindings = useShortcutBindings(host);

  // Both through refs, so the listener is attached once and never has to be
  // torn down and rebuilt — a rebinding or a re-render must not be able to lose
  // a keypress in the gap.
  const current = useRef(bindings);
  current.current = bindings;
  const page = useRef(actions);
  page.current = actions;

  useEffect(() => {
    const cuekards = window.cuekards;
    const specs = shortcutsFor(host);
    /**
     * What each command does. Built once, when the prompter mounts.
     *
     * Total over every command in the table, both hosts', and it is the host's
     * *rows* that decide which of them can ever be reached — four of these
     * describe a window over other applications or footage on a disk, and a tab
     * has neither. Writing the table whole is what makes it exhaustive: a
     * command added to the table and not to this is a compile error, wherever it
     * ends up being reachable from.
     */
    const run: Record<ShortcutCommand, () => void> = {
      record: () => cuekards.startRecording(),
      pauseResume: () => cuekards.togglePauseRecording(),
      stop: () => cuekards.stopRecording(),
      // Position. The escape hatch, in every mode (FR-36).
      nudgeBack: () => cuekards.nudgePosition(-1),
      nudgeForward: () => cuekards.nudgePosition(1),
      restart: () => cuekards.restartPosition(),
      resync: () => cuekards.resyncFollower(),
      cycleMode: () => cuekards.cycleScrollMode(),
      // The live prompter, never the stored default (FR-63).
      fontUp: () => cuekards.stepFontSize(1),
      fontDown: () => cuekards.stepFontSize(-1),
      // The live window, likewise — and a window is the thing a tab is not.
      opacityUp: () => cuekards.stepOpacity(1),
      opacityDown: () => cuekards.stepOpacity(-1),
      refresh: () => page.current.refresh(),
      // A note about the footage, which leaves the take exactly as it was.
      markGood: () => cuekards.markTake("good"),
      markBad: () => cuekards.markTake("bad"),
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (typingInto(event.target as EventTargetLike | null)) return;
      const command = commandFor(specs, current.current, event);
      if (command === null) return;
      event.preventDefault();
      run[command]();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}
